import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Container, Typography, Grid, FormControl, InputLabel, Select, Switch,
  MenuItem, CircularProgress, Paper, Alert, Tooltip as MuiTooltip, IconButton, Card,
  CardContent, FormGroup, FormControlLabel, Checkbox
} from '@mui/material';
import { AreaChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTranslation } from 'react-i18next';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TimelineIcon from '@mui/icons-material/Timeline';
import supabase from '../../../utils/supabase';

const RANK_COLORS = [
  '#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F',
  '#FFBB28', '#FF8042', '#a4de6c', '#d0ed57', '#ff7300', '#413ea0'
];

const calculateLinearRegression = (data) => {
  if (!data || data.length < 2) {
    return { slope: null, intercept: null, rSquared: null };
  }
  const n = data.length;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2 = 0;
  const points = [];

  data.forEach(point => {
    const x = point.year;
    const y = Object.keys(point).reduce((total, key) => key !== 'year' ? total + point[key] : total, 0);
    points.push({ x, y });

    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumX2 += x * x;
  });

  const numerator = n * sumXY - sumX * sumY;
  const denominator = n * sumX2 - sumX * sumX;

  if (denominator === 0) {
    return { slope: null, intercept: null, rSquared: null };
  }

  const slope = numerator / denominator;
  const intercept = (sumY / n) - slope * (sumX / n);
  const meanY = sumY / n;

  let ssTot = 0;
  let ssRes = 0;

  points.forEach(point => {
    const predictedY = slope * point.x + intercept;
    ssTot += Math.pow(point.y - meanY, 2);
    ssRes += Math.pow(point.y - predictedY, 2);
  });

  const rSquared = ssTot === 0 ? 1 : 1 - (ssRes / ssTot);

  return { slope, intercept, rSquared };
};

const CumulativeDescriptionTrends = () => {
  const { t } = useTranslation(['analysis', 'common']);
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [rankFilter, setRankFilter] = useState('All');
  const [subfamilyFilter, setSubfamilyFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');

  const [rankOptions, setRankOptions] = useState([]);
  const [subfamilyOptions, setSubfamilyOptions] = useState([]);
  const [fullYearRange, setFullYearRange] = useState([0, 0]);
  const [startYear, setStartYear] = useState(0);
  const [endYear, setEndYear] = useState(0);

  const [resetCumulative, setResetCumulative] = useState(false);
  const [showLogistic, setShowLogistic] = useState(false);
  const [showGompertz, setShowGompertz] = useState(false);
  const [curveFitResults, setCurveFitResults] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const allData = [];
      let page = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error: rpcError } = await supabase.rpc('get_yearly_taxa_stats', { p_limit: pageSize, p_offset: page * pageSize });
        if (rpcError) throw rpcError;
        if (data && data.length > 0) {
          allData.push(...data);
          if (data.length < pageSize) hasMore = false;
          else page++;
        } else {
          hasMore = false;
        }
      }
      setRawData(allData);
      const ranks = [...new Set(allData.map(item => item.rank))].sort();
      const subfamilies = [...new Set(allData.map(item => item.subfamily_name))].filter(Boolean).sort();
      setRankOptions(['All', ...ranks]);
      setSubfamilyOptions(['All', ...subfamilies]);
    } catch (e) {
      console.error("Failed to fetch taxa trends:", e);
      setError(t('error.fetchMessage'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const titleSx = {
    fontWeight: 800,
    letterSpacing: "0.01em",
    color: "text.primary",
    textAlign: "center",
    mb: 1,
  };

  const subtitleSx = {
    textAlign: "center",
    whiteSpace: "pre-line",
    color: "text.secondary",
    maxWidth: 820,
    mx: "auto",
    mb: 4,
  };

  useEffect(() => {
    if (rawData.length > 0 && fullYearRange[0] === 0) {
      const years = rawData.map(item => item.authority_year).filter(Boolean);
      if (years.length > 0) {
        const min = Math.min(...years);
        const max = Math.max(...years);
        setFullYearRange([min, max]);
        setStartYear(min);
        setEndYear(max);
      }
    }
  }, [rawData, fullYearRange]);

  const yearOptions = useMemo(() => {
    if (fullYearRange[0] === 0) return [];
    const options = [];
    for (let y = fullYearRange[0]; y <= fullYearRange[1]; y++) {
      options.push(y);
    }
    return options;
  }, [fullYearRange]);

  const handleStartYearChange = (event) => {
    const newStartYear = event.target.value;
    setStartYear(newStartYear);
    if (newStartYear > endYear) setEndYear(newStartYear);
  };

  const handleEndYearChange = (event) => {
    const newEndYear = event.target.value;
    setEndYear(newEndYear);
    if (newEndYear < startYear) setStartYear(newEndYear);
  };

  const { baseChartData, allRanks, totalCount } = useMemo(() => {
    const filteredRawData = rawData.filter(item => {
        const rankMatch = rankFilter === 'All' || item.rank === rankFilter;
        const subfamilyMatch = subfamilyFilter === 'All' || item.subfamily_name === subfamilyFilter;
        const statusMatch = statusFilter === 'All' || (statusFilter === 'Valid' && item.status === 'valid') || (statusFilter === 'Invalid' && item.status !== 'valid');
        return rankMatch && subfamilyMatch && statusMatch;
    });

    const dataInPeriod = filteredRawData.filter(item => item.authority_year >= startYear && item.authority_year <= endYear);
    
    if (dataInPeriod.length === 0 || startYear === 0) {
      return { baseChartData: [], allRanks: [], totalCount: 0 };
    }
    
    const totalCount = dataInPeriod.reduce((acc, item) => acc + item.count, 0);
    const ranks = [...new Set(dataInPeriod.map(item => item.rank))].sort();

    const yearlyCountsByRank = dataInPeriod.reduce((acc, item) => {
      const { authority_year: year, rank, count } = item;
      if (year) {
        if (!acc[year]) acc[year] = {};
        acc[year][rank] = (acc[year][rank] || 0) + count;
      }
      return acc;
    }, {});

    const chartData = [];
    let cumulativeCountsByRank = {};
    ranks.forEach(rank => { cumulativeCountsByRank[rank] = 0; });

    if (!resetCumulative) {
      filteredRawData.filter(item => item.authority_year < startYear).forEach(item => {
        if (ranks.includes(item.rank)) {
          cumulativeCountsByRank[item.rank] = (cumulativeCountsByRank[item.rank] || 0) + item.count;
        }
      });
    }

    for (let year = startYear; year <= endYear; year++) {
      const cumulativeYearData = { year };
      const countsForYear = yearlyCountsByRank[year] || {};
      ranks.forEach(rank => {
        const count = countsForYear[rank] || 0;
        cumulativeCountsByRank[rank] += count;
        cumulativeYearData[rank] = cumulativeCountsByRank[rank];
      });
      chartData.push(cumulativeYearData);
    }

    return { baseChartData: chartData, allRanks: ranks, totalCount };
  }, [rawData, rankFilter, subfamilyFilter, statusFilter, startYear, endYear, resetCumulative]);

  useEffect(() => {
    const fetchCurveFits = async () => {
      if (!baseChartData || baseChartData.length < 3) {
        setCurveFitResults(null);
        return;
      }
      try {
        const pointsForFit = baseChartData.map(d => ({
          year: d.year,
          total: Object.keys(d).reduce((sum, key) => (key !== 'year') ? sum + d[key] : sum, 0)
        }));

        const { data, error } = await supabase.functions.invoke('fit-curves', {
          body: { data: pointsForFit },
        });

        if (error) throw error;
        setCurveFitResults(data);
      } catch (e) {
        console.error("Failed to fetch curve fits:", e);
        setCurveFitResults(null);
      }
    };
    fetchCurveFits();
  }, [baseChartData]);

  const { chartData, linearTrendStats } = useMemo(() => {
    if (!baseChartData || baseChartData.length === 0) {
      return { chartData: [], linearTrendStats: { slope: null, intercept: null, rSquared: null } };
    }

    const linearTrendStats = calculateLinearRegression(baseChartData);
    
    const finalChartData = baseChartData.map(d => {
      const newPoint = { ...d };
      if (linearTrendStats.slope !== null) {
        newPoint.linearTrend = Math.max(0, linearTrendStats.slope * d.year + linearTrendStats.intercept);
      }
      
      return newPoint;
    });

    if (curveFitResults) {
      const logisticFit = curveFitResults.find(f => f.model_name === 'logistic');
      if (logisticFit?.params) {
        finalChartData.forEach(d => {
          d.logisticTrend = logisticFit.params.L / (1 + Math.exp(-logisticFit.params.k * (d.year - logisticFit.params.x0)));
        });
      }

      const gompertzFit = curveFitResults.find(f => f.model_name === 'gompertz');
      if (gompertzFit?.params) {
        finalChartData.forEach(d => {
          d.gompertzTrend = gompertzFit.params.a_gompertz * Math.exp(-gompertzFit.params.b_gompertz * Math.exp(-gompertzFit.params.c_gompertz * d.year));
        });
      }
    }

    return { chartData: finalChartData, linearTrendStats };
  }, [baseChartData, curveFitResults]);

  return (
    <Box p={{ xs: 2, md: 4 }}>
      <Container maxWidth="xl">
        <Typography variant="h2" sx={titleSx}>
          {t('cumulativeTrends.title')}
        </Typography>
        <Typography variant="body1" sx={subtitleSx}>
          {t('cumulativeTrends.description')}
        </Typography>

        {loading && <CircularProgress sx={{ display: 'block', margin: 'auto', my: 4 }} />}
        
        {error && (
          <Alert severity="error" sx={{ my: 2 }}>
            <Typography variant="h6">{t('error.title')}</Typography>
            <Typography>{error}</Typography>
            <Typography sx={{ mt: 1 }}>
              <strong>{t('error.adminHintTitle')}</strong> {t('error.adminHintBody')}
            </Typography>
          </Alert>
        )}

        {!loading && !error && (
          <>
            <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 3, backdropFilter: 'blur(5px)', backgroundColor: 'rgba(30,30,30,0.5)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SettingsIcon sx={{ mr: 1 }} />
                <Typography variant="h6">{t('parametersTitle')}</Typography>
              </Box>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}><FormControl fullWidth><InputLabel>{t('common:subfamily')}</InputLabel><Select value={subfamilyFilter} label={t('common:subfamily')} onChange={(e) => setSubfamilyFilter(e.target.value)}>{subfamilyOptions.map(sf => <MenuItem key={sf} value={sf}>{sf}</MenuItem>)}</Select></FormControl></Grid>
                <Grid item xs={12} sm={6} md={3}><FormControl fullWidth><InputLabel>{t('common:rank')}</InputLabel><Select value={rankFilter} label={t('common:rank')} onChange={(e) => setRankFilter(e.target.value)}>{rankOptions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}</Select></FormControl></Grid>
                <Grid item xs={12} sm={6} md={3}><FormControl fullWidth><InputLabel>{t('common:status')}</InputLabel><Select value={statusFilter} label={t('common:status')} onChange={(e) => setStatusFilter(e.target.value)}><MenuItem value="All">{t('common:all')}</MenuItem><MenuItem value="Valid">{t('common:valid')}</MenuItem><MenuItem value="Invalid">{t('common:invalid')}</MenuItem></Select></FormControl></Grid>
                <Grid item xs={6} sm={3} md={1.5}><FormControl fullWidth><InputLabel>{t('startYear')}</InputLabel><Select value={startYear} label={t('startYear')} onChange={handleStartYearChange} disabled={yearOptions.length === 0}>{yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}</Select></FormControl></Grid>
                <Grid item xs={6} sm={3} md={1.5}><FormControl fullWidth><InputLabel>{t('endYear')}</InputLabel><Select value={endYear} label={t('endYear')} onChange={handleEndYearChange} disabled={yearOptions.length === 0}>{yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}</Select></FormControl></Grid>
              </Grid>
            </Paper>

            <Paper elevation={3} sx={{ p: { xs: 1, sm: 2, md: 3 }, mb: 3, backdropFilter: 'blur(5px)', backgroundColor: 'rgba(30,30,30,0.5)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TimelineIcon sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ flexGrow: 1 }}>{t('cumulativeTrends.chartTitle')}</Typography>
                <FormGroup row>
                  <FormControlLabel control={<Switch checked={resetCumulative} onChange={(e) => setResetCumulative(e.target.checked)} />} label={t('cumulativeTrends.resetCumulative')} />
                  <FormControlLabel control={<Checkbox checked={showLogistic} onChange={(e) => setShowLogistic(e.target.checked)} disabled={!curveFitResults || !curveFitResults.find(f => f.model_name === 'logistic')?.params} />} label={t('cumulativeTrends.showLogistic')} />
                  <FormControlLabel control={<Checkbox checked={showGompertz} onChange={(e) => setShowGompertz(e.target.checked)} disabled={!curveFitResults || !curveFitResults.find(f => f.model_name === 'gompertz')?.params} />} label={t('cumulativeTrends.showGompertz')} />

                </FormGroup>
              </Box>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis label={{ value: t('cumulativeCount'), angle: -90, position: 'insideLeft' }} />
                    <Tooltip contentStyle={{ backgroundColor: 'rgba(30, 30, 30, 0.95)', border: '1px solid rgba(127, 255, 212, 0.3)', borderRadius: '8px' }} />
                    <Legend />
                    {allRanks.map((rank, index) => (
                      <Area key={rank} type="monotone" dataKey={rank} stackId="1" stroke={RANK_COLORS[index % RANK_COLORS.length]} fill={RANK_COLORS[index % RANK_COLORS.length]} name={rank} />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', backgroundColor: 'transparent' }}>
                  <Typography color="text.secondary">{t('noData')}</Typography>
                </Paper>
              )}
            </Paper>

            {chartData.length > 0 && (
              <Box>
                <Box mb={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AssessmentIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">{t('resultsTitle')}</Typography>
                  </Box>
                  <Card sx={{ backgroundColor: 'rgba(0,0,0,0.2)', display: 'inline-block', minWidth: 275 }}>
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <Typography color="text.secondary" gutterBottom>{t('taxaNumberTitle')}</Typography>
                      <Typography variant="h4" component="div">{totalCount.toLocaleString()}</Typography>
                    </CardContent>
                  </Card>
                </Box>

                {(linearTrendStats.slope !== null || (curveFitResults && curveFitResults.some(f => f.r_squared !== null))) && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <AssessmentIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">{t('linearRegressionTitle')}</Typography>
                    </Box>
                    <Grid container spacing={3}>
                      {/* 線形回帰の統計 */}
                      {linearTrendStats.slope !== null && linearTrendStats.rSquared !== null && (
                        <>
                          <Grid item xs={12} sm={6} md={4}>
                            <Card sx={{ backgroundColor: 'rgba(0,0,0,0.2)', height: '100%' }}>
                              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                  <Typography color="text.secondary" gutterBottom>{t('trendSlope')} ({t('cumulativeTrends.linearCurveName')})</Typography>
                                  <MuiTooltip title={t('stats.trendTooltip')}><IconButton size="small"><InfoOutlinedIcon fontSize="inherit" /></IconButton></MuiTooltip>
                                </Box>
                                <Typography variant="h4" component="div">{linearTrendStats.slope.toFixed(2)}</Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}> {/* R2値は別のカードとして表示 */}
                            <Card sx={{ backgroundColor: 'rgba(0,0,0,0.2)', height: '100%' }}>
                              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                  <Typography color="text.secondary" gutterBottom>{t('rSquared')} ({t('cumulativeTrends.linearCurveName')})</Typography>
                                  <MuiTooltip title={t('stats.rSquaredTooltip')}><IconButton size="small"><InfoOutlinedIcon fontSize="inherit" /></IconButton></MuiTooltip>
                                </Box>
                                <Typography variant="h4" component="div">{linearTrendStats.rSquared.toFixed(3)}</Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        </>
                      )}

                      {/* 非線形回帰の統計 (サーバーからの結果を使用) */}
                      {curveFitResults && curveFitResults.map((fit, index) => {
                        if (fit.r_squared === null) return null;
                        let modelNameKey = '';
                        switch (fit.model_name) {
                          case 'logistic':
                            modelNameKey = 'cumulativeTrends.logisticCurveName';
                            break;
                          case 'gompertz':
                            modelNameKey = 'cumulativeTrends.gompertzCurveName';
                            break;
                          case 'exponential':
                            modelNameKey = 'cumulativeTrends.exponentialCurveName';
                          default:
                            return null;
                        }
                        return (
                          <Grid item xs={12} sm={6} md={4} key={fit.model_name}>
                            <Card sx={{ backgroundColor: 'rgba(0,0,0,0.2)', height: '100%' }}>
                              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                  <Typography color="text.secondary" gutterBottom>{t('rSquared')} ({t(modelNameKey)})</Typography>
                                  <MuiTooltip title={t('stats.rSquaredTooltip')}><IconButton size="small"><InfoOutlinedIcon fontSize="inherit" /></IconButton></MuiTooltip>
                                </Box>
                                <Typography variant="h4" component="div">{fit.r_squared.toFixed(3)}</Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default CumulativeDescriptionTrends;
